import React from 'react';

/**
 * The page title band used under the menu bar: title, one-line subtitle, action buttons on the right
 * and an optional row of tabs. It breaks out of the Layout's padding so the line under it runs edge to edge.
 * Tabs: [{ id, label, count? }] (test ids are `tabTestIdPrefix` + id); `activeTab` is the selected id and `onTab(id)` is called on click.
 */
const PageHeader = ({ title, subtitle, actions, tabs, activeTab, onTab, tabTestIdPrefix }) => (
  <div className="-mx-6 -mt-6 mb-8 bg-white border-b border-slate-200" data-testid="page-header">
    <div className={`max-w-[1240px] mx-auto px-8 pt-7 ${tabs?.length ? '' : 'pb-5'}`}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-[30px] leading-tight font-semibold tracking-tight text-[#14213d]">{title}</h1>
          {subtitle && <p className="text-[#566078] mt-0.5">{subtitle}</p>}
        </div>
        {actions && <div className={`flex flex-wrap gap-2.5 ${tabs?.length ? 'pb-3.5' : ''}`}>{actions}</div>}
      </div>
      {tabs?.length > 0 && (
        <div className="flex flex-wrap gap-7 mt-4" role="tablist">
          {tabs.map((t) => {
            const on = t.id === activeTab;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={on}
                data-testid={tabTestIdPrefix ? `${tabTestIdPrefix}${t.id}` : undefined}
                onClick={() => onTab?.(t.id)}
                className={`-mb-px pt-2 pb-3 font-semibold border-b-[3px] transition-colors ${
                  on ? 'text-[#14213d] border-[#14213d]' : 'text-[#566078] border-transparent hover:text-[#14213d]'
                }`}
              >
                {t.label}
                {t.count !== undefined && <span className="ml-1.5 font-medium text-[#566078]">{t.count}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  </div>
);

/** Shared button looks for page headers, matching the quiet navy design. */
export const headerButtonClass =
  'inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 bg-white font-semibold text-[#14213d] transition-colors hover:border-[#14213d]';
export const headerPrimaryClass =
  'inline-flex items-center gap-2 px-[18px] py-2 rounded-lg bg-[#14213d] font-semibold text-white transition-colors hover:bg-[#0c1730]';

export default PageHeader;
